import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { useState } from 'react'
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued'

import { Button } from '@/components'
import { Modal } from '@/components/Modal/Modal'
import { useAppContext } from '@/hooks'
import {
	DiffFooterActions,
	DiffHeaderFlex,
	DiffViewToggleButton,
	LoadingPlaceholder,
	StyledDiffWrap,
} from '@/pages/User/pages/Settings/pages/Templates/styles'
import { useGetNodeHistoryDetailByVersionUsingGetQuery } from '@/rtkApi/mddApiSliceGenerated'

const MODAL_MIN_WIDTH = 1000

const MODAL_CONTENT_STYLE = {
	width: MODAL_MIN_WIDTH,
	minWidth: MODAL_MIN_WIDTH,
} as const

const MODAL_BODY_STYLE = {
	display: 'flex',
	flexDirection: 'column',
} as const

const MODAL_HEADER_STYLE = { paddingBottom: 0 } as const

const DIFF_VIEWER_STYLES = {
	contentText: { fontFamily: 'monospace' },
} as const

function normalizeLineEndings(text: string): string {
	return text.replace(/\r\n|\r/g, '\n')
}

function tryFormatJson(data: string): string {
	try {
		const parsed = JSON.parse(data)
		return JSON.stringify(parsed, null, 2)
	} catch {
		return data
	}
}

type ViewMode = 'split' | 'unified'

export type VersionDiffModalProps = {
	/** Version from the history row (left side) */
	historyVersion: number
	onClose: () => void
	structureId: number
	structureName?: string
	/** Current / working version (right side) */
	workingVersion: number
}

export const VersionDiffModal = ({
	structureId,
	structureName,
	historyVersion,
	workingVersion,
	onClose,
}: VersionDiffModalProps) => {
	const { t } = useAppContext()
	const [viewMode, setViewMode] = useState<ViewMode>('split')

	const historyDetail = useGetNodeHistoryDetailByVersionUsingGetQuery(
		{ structureId, version: historyVersion },
		{ skip: !structureId || historyVersion == null },
	)
	const workingDetail = useGetNodeHistoryDetailByVersionUsingGetQuery(
		{ structureId, version: workingVersion },
		{ skip: !structureId || workingVersion == null },
	)

	const leftContent = historyDetail.data?.data ?? ''
	const rightContent = workingDetail.data?.data ?? ''
	const normalizedLeft = normalizeLineEndings(tryFormatJson(leftContent))
	const normalizedRight = normalizeLineEndings(tryFormatJson(rightContent))

	const isLoading =
		historyDetail.isLoading ||
		workingDetail.isLoading ||
		(historyDetail.data == null && workingDetail.data == null)

	const leftTitle = `${t('VERSION_TO_COMPARE')} ${historyDetail.data?.version ?? historyVersion}`
	const rightTitle = `${t('VERSION')} ${workingDetail.data?.version ?? workingVersion}`

	const header = () => (
		<DiffHeaderFlex>
			<span>{structureName ?? t('VERSIONS')}</span>
			<DiffViewToggleButton
				testId="version-diff-view-toggle"
				onClick={() => setViewMode((m) => (m === 'split' ? 'unified' : 'split'))}
			>
				{viewMode === 'split'
					? t('TEMPLATE_DIFF_UNIFIED')
					: t('TEMPLATE_DIFF_SIDE_BY_SIDE')}
			</DiffViewToggleButton>
		</DiffHeaderFlex>
	)

	return (
		<Modal
			open
			onClose={onClose}
			contentStyle={MODAL_CONTENT_STYLE}
			bodyStyle={MODAL_BODY_STYLE}
			resizable
			maximizeButtonVisible
			maximized
			headerStyle={MODAL_HEADER_STYLE}
			header={header}
		>
			<StyledDiffWrap data-testid="version-diff-content">
				{isLoading ? (
					<LoadingPlaceholder data-testid="version-diff-loading">
						{t('LOADING')}
					</LoadingPlaceholder>
				) : (
					<ReactDiffViewer
						oldValue={normalizedLeft}
						newValue={normalizedRight}
						splitView={viewMode === 'split'}
						compareMethod={DiffMethod.LINES}
						leftTitle={leftTitle}
						rightTitle={rightTitle}
						styles={DIFF_VIEWER_STYLES}
					/>
				)}
			</StyledDiffWrap>
			<DiffFooterActions>
				<Button
					testId="version-diff-close"
					onClick={onClose}
					schema="transparent"
					icon={faTimes}
				>
					{t('CLOSE')}
				</Button>
			</DiffFooterActions>
		</Modal>
	)
}
